/**
 * 登录 / 鉴权接口
 *
 * 桌面 + Web 端无微信环境，统一走「手机号 + 验证码」（带图形滑块验证码防刷）。
 */
import request from '../xajax'
import type { ApiResponse } from '../interfaces'
import type { ClientType } from '@/utils/client'

export interface FrontUserInfo {
  avatar?: string
  dealerId?: string
  nickName?: string
  inviteCode?: string
  mobile?: string
  loginName?: string
  email?: string
  regTime?: string
  status?: number
  gender?: string
  newUser?: boolean
  conflictMobile?: string
  /** 0无 1黄金 2铂金 3钻石 */
  vipLevel?: number
  vipName?: string
  vipExpire?: string
}

export interface FrontLoginInfo {
  accessToken: string
  refreshToken?: string
  expiresIn?: number
  openid?: string
  userInfo?: FrontUserInfo
}

/**
 * 账号密码登录参数
 *
 * name 可以是手机号或登录名（后端按 ^1[3-9]\d{9}$ 判断，前端无需区分）；
 * cipher 与移动端一致，明文提交（由后端自行加密存储）。
 */
export type LoginParams = {
  /** 用户名（手机号或登录名） */
  name: string
  /** 密码 */
  cipher: string
  /**
   * 客户端类型：web PC 网页 / app 桌面版
   * 由 src/utils/client.ts 的 clientType() 统一判定，调用方不要自己拼
   */
  client: ClientType
  /** 客户端对应的 openid：桌面 / Web 端拿不到微信身份，恒为空（不传） */
  openid?: string
}

export interface CaptchaVo {
  captchaId: string
  backgroundImage: string
  sliderImage: string
  y: number
}

export interface SmsCaptchaParams {
  mobile: string
  captchaId?: string
  captchaCode?: string
}

export interface MobileLoginParams {
  mobile: string
  validCode: string
  captchaId?: string
  inviteCode?: string
  openid?: string
  /** wxgzh / wxxcx / douyin */
  type?: string
}

/**
 * 扫码登录：Web / 桌面端拿不到微信环境，走「临时场景值 + 轮询」，
 * 与移动端「微信授权登录」是同一套账号体系（openid 绑定）。
 *
 * 两种码对应两种场景（页面上是两个独立入口）：
 *  - gzh：微信扫码（公众号 / 开放平台带参二维码）
 *  - xcx：小程序扫码（小程序码，扫码后进入小程序确认页）
 *
 * 流程：qrcode(kind) 取场景值 -> 前端渲染成二维码（也可由后端直接下发图片）
 *       -> 用户扫码确认 -> 前端轮询 check()，status=success 时拿到 token
 */
export type WxLoginKind = 'gzh' | 'xcx'
export type WxLoginStatus = 'wait' | 'scanned' | 'success' | 'expired'

export interface WxLoginSceneVo {
  /** 场景值：二维码内容（小程序码 / 带 scene 的链接） */
  scene: string
  /** 有效期（秒），过期后需刷新二维码 */
  expireSeconds: number
  /** 后端已生成好的二维码图片（有的话直接用，没有则由前端用 scene 渲染） */
  qrImage?: string
}

export interface WxLoginCheckVo {
  status: WxLoginStatus
  /** status === 'success' 时有值 */
  accessToken?: string
  refreshToken?: string
  openid?: string
  userInfo?: FrontUserInfo
}

export const apiWxLogin = {
  /** 获取扫码登录场景值（含二维码内容）：kind=gzh 微信扫码 / xcx 小程序码 */
  qrcode: (kind: WxLoginKind): Promise<ApiResponse<WxLoginSceneVo>> =>
    request.get(`/client/wechat/${kind}/login-qrcode`),

  /** 轮询登录结果：wait / scanned / success / expired（场景值里已含 kind） */
  check: (scene: string): Promise<ApiResponse<WxLoginCheckVo>> =>
    request.get(`/client/wechat/login-check?scene=${encodeURIComponent(scene)}`)
}

export const apiAuth = {
  /** 账号密码登录 */
  login: (data: LoginParams): Promise<ApiResponse<FrontLoginInfo>> => request.post('/client/nr/auth/login', data),

  /** 图形滑块验证码 */
  captcha: (): Promise<ApiResponse<CaptchaVo>> => request.get('/client/auth/captcha'),

  /** 发送短信验证码 */
  sendSms: (data: SmsCaptchaParams): Promise<ApiResponse<boolean>> => request.post('/client/auth/captcha/sms', data),

  /** 手机号 + 验证码登录（未注册自动创建） */
  mobileLogin: (data: MobileLoginParams): Promise<ApiResponse<FrontLoginInfo>> =>
    request.post('/client/auth/captcha/login', data)
}
